// en-GB formatting for every number, date, month name, duration, cost (with and without the currency sign), size and requirement value shown in the UI (no sentence concatenation in components).
import type { BaseQuantity, Comparator } from "@/api/__generated__";

const LOCALE = "en-GB";

const integerFormat = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });
const compactFormat = new Intl.NumberFormat(LOCALE, { notation: "compact", maximumFractionDigits: 1 });
const usdFormat = new Intl.NumberFormat(LOCALE, {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const usdSubCentFormat = new Intl.NumberFormat(LOCALE, {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});
const usdAmountFormat = new Intl.NumberFormat(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const monthNameFormat = new Intl.DateTimeFormat(LOCALE, { month: "long", timeZone: "UTC" });
const ONE_CENT = 0.01;
const dateFormat = new Intl.DateTimeFormat(LOCALE, {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});
const dateTimeFormat = new Intl.DateTimeFormat(LOCALE, {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});
const localDateFormat = new Intl.DateTimeFormat(LOCALE, {
  day: "numeric",
  month: "short",
  year: "numeric",
});

export function formatNumber(value: number | null | undefined, maximumFractionDigits = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "–";
  }
  if (maximumFractionDigits === 0) {
    return integerFormat.format(value);
  }
  return new Intl.NumberFormat(LOCALE, { maximumFractionDigits }).format(value);
}

export function formatCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "–";
  }
  return compactFormat.format(value).toUpperCase();
}

export function formatPercent(fraction: number | null | undefined, maximumFractionDigits = 0): string {
  if (fraction === null || fraction === undefined || Number.isNaN(fraction)) {
    return "–";
  }
  return new Intl.NumberFormat(LOCALE, { style: "percent", maximumFractionDigits }).format(fraction);
}

function toDate(value: string | Date): Date | null {
  const date = typeof value === "string" ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(value: string | Date | null | undefined): string {
  if (value === null || value === undefined) {
    return "–";
  }
  const date = toDate(value);
  return date ? dateFormat.format(date) : "–";
}

export function formatLocalDate(value: string | Date | null | undefined): string {
  if (value === null || value === undefined) {
    return "–";
  }
  const date = toDate(value);
  return date ? localDateFormat.format(date) : "–";
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (value === null || value === undefined) {
    return "–";
  }
  const date = toDate(value);
  return date ? dateTimeFormat.format(date) : "–";
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) {
    return "–";
  }
  if (seconds < 1) {
    return `${formatNumber(seconds * 1000)} ms`;
  }
  if (seconds < 60) {
    return `${formatNumber(seconds, seconds < 10 ? 2 : 1)} s`;
  }
  const totalMinutes = Math.floor(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const rest = Math.round(seconds % 60);
  if (hours > 0) {
    return `${hours} h ${minutes} min`;
  }
  return rest > 0 ? `${minutes} min ${rest} s` : `${minutes} min`;
}

export function formatCurrencyUsd(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "–";
  }
  const subCent = value !== 0 && Math.abs(value) < ONE_CENT;
  return (subCent ? usdSubCentFormat : usdFormat).format(value);
}

export function formatUsdAmount(value: number): string {
  return usdAmountFormat.format(value);
}

export function formatMonthName(month: string): string {
  const [year, number] = month.split("-").map(Number);
  if (year === undefined || number === undefined || !Number.isInteger(year) || !Number.isInteger(number)) {
    return month;
  }
  return monthNameFormat.format(Date.UTC(year, number - 1, 1));
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) {
    return "–";
  }
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${formatNumber(value, unit === 0 ? 0 : 1)} ${units[unit] ?? "B"}`;
}

const COMPARATOR_SYMBOLS: Record<Comparator, string> = {
  ge: "≥",
  le: "≤",
  gt: ">",
  lt: "<",
  eq: "=",
  between: "",
  outside: "outside",
  none: "",
};

const SILENT_BASES = new Set<BaseQuantity>(["absolute", "not_applicable", "other"]);

export interface FormattableValue {
  comparator: Comparator;
  value: number | null;
  value_min: number | null;
  value_max: number | null;
  unit: string | null;
  base: BaseQuantity;
}

export function formatValue(requirement: FormattableValue): string {
  const unit = requirement.unit ? ` ${requirement.unit}` : "";
  const base = SILENT_BASES.has(requirement.base) ? "" : ` (${requirement.base})`;
  const { value_min: min, value_max: max } = requirement;
  if (min !== null && max !== null) {
    const range = `${formatNumber(min, 4)}–${formatNumber(max, 4)}${unit}`;
    return requirement.comparator === "outside" ? `outside ${range}${base}` : `${range}${base}`;
  }
  if (requirement.value === null) {
    return "–";
  }
  const symbol = COMPARATOR_SYMBOLS[requirement.comparator];
  const prefix = symbol ? `${symbol} ` : "";
  return `${prefix}${formatNumber(requirement.value, 4)}${unit}${base}`;
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? "") : "";
  return `${first}${last}`.toUpperCase() || "?";
}

export function plural(count: number, singular: string, pluralForm = `${singular}s`): string {
  return `${formatNumber(count)} ${count === 1 ? singular : pluralForm}`;
}
